import Plyr from 'plyr';

// Playback rates offered in the settings menu.
const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

// Self-hosted Feather sprite. Plyr's default icon URL points at its CDN, which
// the site CSP blocks.
const ICON_URL = '/static/img/video_player/plyr-icons.svg';

// Build a Plyr quality menu from the renditions hls.js parsed. Value 0 is the
// adaptive "Auto" level; the rest are rendition heights, descending. `forced`
// makes Plyr route a change through onChange instead of the media element, so
// switching drives hls.currentLevel. Returns null when there is nothing to
// switch (native HLS on Safari, or a single-rendition stream) — no menu.
function qualityOption(video) {
  const hls = video.hlsInstance;
  if (!hls || !hls.levels || !hls.levels.length) return null;

  const heights = [...new Set(hls.levels.map((level) => level.height))].sort((a, b) => b - a);

  return {
    default: 0,
    options: [0, ...heights],
    forced: true,
    onChange: (height) => {
      if (height === 0) {
        hls.currentLevel = -1;
        return;
      }
      hls.levels.forEach((level, index) => {
        if (level.height === height) hls.currentLevel = index;
      });
    },
  };
}

// Plyr control set for one player, matching the render() markup:
//   no `controls` attribute -> only the centre play button,
//   allow_seek=0            -> full bar without the seek scrubber,
//   otherwise               -> full bar.
function controlLayout(video) {
  if (!video.hasAttribute('controls')) {
    return ['play-large'];
  }
  if (video.dataset.videoAllowSeek === '0') {
    return ['play-large', 'play', 'current-time', 'mute', 'volume', 'settings', 'fullscreen'];
  }
  return ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'settings', 'fullscreen'];
}

// Overlay for a muted autoplay video: a silent looping preview under the centre
// play button and a pulsing, language-specific "turn on sound" image
// (data-video-lang, pl or en with en as the fallback). A transparent
// full-surface button turns the whole player into one click target. Engaging —
// that click, or unmuting from the controls — rewinds to the start, restores
// the original loop flag, unmutes, and dispatches `video:engage`, which is what
// tells the data layer to start counting the view. No-op when not muted.
function mountAutoplayOverlay(player, video) {
  if (!video.muted) return;

  const container = player.elements && player.elements.container;
  if (!container) return;

  const lang = video.dataset.videoLang === 'pl' ? 'pl' : 'en';
  const translate = window.gettext || ((value) => value);

  container.classList.add('video-autoplay');

  const originalLoop = video.loop;
  video.loop = true;

  const cta = document.createElement('div');
  cta.className = `video-unmute-cta video-unmute-cta--${lang}`;

  const catcher = document.createElement('button');
  catcher.type = 'button';
  catcher.className = 'video-autoplay-catcher';
  catcher.setAttribute('aria-label', translate('Kliknij, aby włączyć dźwięk'));

  let engaged = false;
  const engage = () => {
    if (engaged) return;
    engaged = true;

    container.classList.remove('video-autoplay');
    video.loop = originalLoop;
    cta.remove();
    catcher.remove();

    player.muted = false;
    if (player.volume === 0) player.volume = 1;
    player.currentTime = 0;
    const playback = player.play();
    if (playback && typeof playback.catch === 'function') playback.catch(() => {});

    video.dispatchEvent(new CustomEvent('video:engage'));
  };

  catcher.addEventListener('click', engage);
  player.on('volumechange', () => {
    if (!player.muted && player.volume > 0) engage();
  });

  container.appendChild(cta);
  container.appendChild(catcher);
}

// Start muted autoplay. The media source is attached before Plyr initialises,
// so the element's own autoplay already fired and Plyr's autoplay option has
// nothing left to trigger. Start playback explicitly from Plyr's `ready` and
// the media's `canplay` (native HLS on Safari only becomes playable at the
// latter). Muted is required for an unprompted play to be allowed.
function startMutedAutoplay(player, video) {
  if (!video.hasAttribute('autoplay')) return;

  const play = () => {
    player.muted = true;
    const playback = player.play();
    if (playback && typeof playback.catch === 'function') playback.catch(() => {});
  };

  player.once('ready', play);
  video.addEventListener('canplay', play, { once: true });
}

// Wrap one <video> with Plyr and attach the muted-autoplay overlay.
function skin(video) {
  const quality = qualityOption(video);
  const settings = quality ? ['quality', 'speed'] : ['speed'];

  const translate = window.gettext || ((value) => value);

  const player = new Plyr(video, {
    controls: controlLayout(video),
    settings,
    speed: { selected: 1, options: SPEED_OPTIONS },
    ...(quality ? { quality } : {}),
    // Quality value 0 is the adaptive level; without this label Plyr renders it
    // as "0p" instead of "Auto".
    i18n: { qualityLabel: { 0: translate('Auto') } },
    // Each player is independent; without this Plyr would carry a muted-autoplay
    // player's muted/volume state over to the next, unrelated video.
    storage: { enabled: false },
    iconUrl: ICON_URL,
    autoplay: video.hasAttribute('autoplay'),
  });

  mountAutoplayOverlay(player, video);
  startMutedAutoplay(player, video);
}

// Skin every self-hosted player once. The quality menu needs hls.levels, so
// where hls.js is in use wait for the data layer's `hls:ready`; build at once
// otherwise (native HLS exposes no renditions to switch).
export function init() {
  document.querySelectorAll('video[data-video-hls]').forEach((video) => {
    if (video.dataset.plyrBound) return;
    video.dataset.plyrBound = '1';

    if (video.hlsInstance && !video.dataset.hlsReady) {
      video.addEventListener('hls:ready', () => skin(video), { once: true });
    } else {
      skin(video);
    }
  });
}
